import { useEffect, useRef } from "react";
import classNames from "classnames";
import ExpandMoreOutlinedIcon from "@mui/icons-material/ExpandMoreOutlined";
import MUIAccordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";

import Typography from "features/components/Typography";

import { DEFAULT_ACCORDION_SIZE, DEFAULT_ACCORDION_VARIANT } from "./constants";
import { AccordionProps } from "./types";
import {
  getDetailsVariantClassName,
  getSizeClassName,
  getVariantClassName,
} from "./utils";

const Accordion = ({
  id,
  title,
  children,
  expanded,
  onChange,
  rightContent,
  disabled,
  className,
  size = DEFAULT_ACCORDION_SIZE,
  variant = DEFAULT_ACCORDION_VARIANT,
  expandIconPosition = "right",
  testId,
  ...otherProps
}: AccordionProps) => {
  // scroll to the accordion element after it is expanded by user interaction
  // (not on initial mount or when expanded by default)
  const accordionRef = useRef<HTMLDivElement>(null);
  const prevExpandedRef = useRef<boolean | undefined>(undefined);

  useEffect(() => {
    // only scroll if expanded changed from false to true (user clicked to expand)
    const wasCollapsed = prevExpandedRef.current === false;
    prevExpandedRef.current = expanded;

    if (wasCollapsed && expanded && accordionRef?.current) {
      setTimeout(() => {
        accordionRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
          inline: "nearest",
        });
      }, 0);
    }
  }, [expanded]);

  const sizeClassName = getSizeClassName(size);
  const variantClassName = getVariantClassName(variant);

  const titleElement =
    typeof title === "string" ? (
      <Typography variant="h5">{title}</Typography>
    ) : (
      title
    );

  return (
    <MUIAccordion
      {...otherProps}
      variant="outlined"
      key={id}
      ref={accordionRef}
      data-testid={testId}
      expanded={expanded}
      onChange={onChange && onChange}
      square
      disabled={disabled}
      disableGutters
      className={classNames(sizeClassName, variantClassName, className)}
      slotProps={{
        transition: {
          timeout: 0,
          unmountOnExit: true,
        },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreOutlinedIcon sx={{ fontSize: 20 }} />}
        slotProps={{
          expandIconWrapper:
            expandIconPosition === "left"
              ? { sx: { order: -1, mr: 1, ml: 0 } }
              : undefined,
          content: {
            sx: { minWidth: 0, overflow: "hidden" },
          },
        }}
      >
        <div
          className={classNames(
            "flex flex-1 justify-between items-center min-w-0",
            { "mr-3": expandIconPosition === "right" }
          )}
        >
          <div className="min-w-0 overflow-hidden flex items-center">
            {titleElement}
          </div>
          {rightContent}
        </div>
      </AccordionSummary>
      <AccordionDetails className={getDetailsVariantClassName(variant)}>
        {children}
      </AccordionDetails>
    </MUIAccordion>
  );
};

export default Accordion;
